import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { buildFrameDocument } from "./frameDocument";
import { frameCore } from "./frameCore";
import DropdownMenu from "../../dropdown-menu/DropdownMenu";
import TinyContextMenu from "../../tiny-context-menu/TinyContextMenu";
import {
  FiRotateCcw,
  FiRotateCw,
  FiType,
  FiImage,
  FiSquare,
  FiCircle,
  FiTriangle,
  FiStar,
  FiSmile,
  FiCopy,
  FiGrid,
  FiArrowUp,
  FiArrowDown,
  FiTrash2,
  FiEdit3,
  FiPlay,
  FiPause,
  FiVolume2,
  FiVolumeX,
} from "react-icons/fi";

// One persistent iframe; document changes travel through the bridge, never srcDoc reloads.
const EMPTY_ASSETS = Object.freeze({});
const FrameSurface = forwardRef(function FrameSurface(
  {
    frame,
    assets = EMPTY_ASSETS,
    theme,
    onReady,
    editable = false,
    active = true,
    disabled = false,
    onChange,
    onEditText,
    onPickAsset,
    onError,
    style,
    className,
    navigation,
    controls = true,
    onPlayback,
    onMediaReady,
    fit = "contain",
    preload = "metadata",
  },
  forwardedRef,
) {
  const channel = useRef("frame_" + frameCore.id()).current;
  const node = useRef(null),
    ready = useRef(false),
    pending = useRef(new Map());
  const latest = useRef(null);
  const menu = useRef(null);
  const [toolState, setToolState] = useState({ selected: [], snap: true }),
    [context, setContext] = useState(null);
  const localBlobs = useRef(new Map()),
    syncVersion = useRef(0),
    syncPending = useRef(Promise.resolve());
  const [error, setError] = useState(""),
    [playback, setPlayback] = useState({});
  latest.current = {
    frame,
    assets,
    theme,
    onReady,
    active,
    disabled,
    onChange,
    onEditText,
    onPickAsset,
    onError,
    onPlayback,
    onMediaReady,
    controls,
    fit,
    preload,
  };
  const html = useMemo(
    () =>
      buildFrameDocument({
        channel,
        editable,
        nativeControls: true,
        externalToolbar: editable,
        autoStyle: editable,
      }),
    [channel, editable],
  );
  const send = (type, data) =>
    node.current?.contentWindow?.postMessage({ channel, type, data }, "*");
  function readTheme() {
    const styles = getComputedStyle(document.body),
      read = (key) => styles.getPropertyValue(key).trim();
    return {
      paper: read("--paper"),
      text: read("--primary"),
      muted: read("--header2"),
      border: read("--border1") || read("--border"),
      surface: read("--background2"),
      accent: read("--brand"),
    };
  }
  useEffect(() => {
    const observer = new MutationObserver(() => synchronize());
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["data-theme", "class", "style"],
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    return () => observer.disconnect();
  }, []);
  const synchronizeNow = async () => {
    if (!ready.current) return;
    const version = ++syncVersion.current,
      value = latest.current;
    const assets = {};
    try {
      await Promise.all(
        frameCore.assetRefs(value.frame).map(async (ref) => {
          const url = value.assets[ref];
          if (typeof url === "string" && url.startsWith("blob:")) {
            // An opaque sandbox cannot read its parent's blob URL. Clone the Blob
            // through the bridge so the sandbox can create its own local URL.
            if (!localBlobs.current.has(url))
              localBlobs.current.set(
                url,
                fetch(url).then((r) => {
                  if (!r.ok)
                    throw new Error(
                      "A local media preview could not be opened.",
                    );
                  return r.blob();
                }),
              );
            assets[ref] = {
              blob: await localBlobs.current.get(url),
              source: url,
            };
          } else if (url) assets[ref] = url;
        }),
      );
    } catch (e) {
      if (version === syncVersion.current) {
        setError(e.message);
        value.onError?.(e.message);
      }
      return;
    }
    if (!ready.current || version !== syncVersion.current) return;
    send("frame", {
      frame: value.frame,
      assets,
      theme: value.theme || readTheme(),
      active: value.active,
      disabled: value.disabled,
      fit: value.fit,
      preload: value.preload,
    });
    value.onReady?.();
  };
  const synchronize = () => (syncPending.current = synchronizeNow());
  useImperativeHandle(forwardedRef, () => ({
    flush: async () => {
      let task;
      do {
        task = syncPending.current;
        await task;
      } while (task !== syncPending.current);
      return new Promise((resolve, reject) => {
        if (!ready.current) {
          reject(new Error("The editor is still loading."));
          return;
        }
        const requestId = frameCore.id();
        const timer = setTimeout(() => {
          pending.current.delete(requestId);
          reject(new Error("The editor did not respond. Try again."));
        }, 6000);
        pending.current.set(requestId, { resolve, reject, timer });
        send("flush", { requestId });
      });
    },
    command: (command, data = {}) => send("command", { command, ...data }),
  }));
  useEffect(() => {
    ready.current = false;
    const listener = (event) => {
      if (
        event.source !== node.current?.contentWindow ||
        event.data?.channel !== channel
      )
        return;
      const { type, data } = event.data,
        callbacks = latest.current;
      if (type === "ready") {
        ready.current = true;
        setError("");
        synchronize();
      }
      if (type === "mediaReady") callbacks.onMediaReady?.(data);
      if (type === "change") callbacks.onChange?.(data);
      if (type === "video") {
        if (callbacks.controls) setPlayback(data);
        callbacks.onPlayback?.(data);
      }
      if (type === "tools")
        setToolState((old) =>
          JSON.stringify(old) === JSON.stringify(data) ? old : data,
        );
      if (type === "context") setContext(data);
      if (type === "editText") callbacks.onEditText?.(data);
      if (type === "asset") callbacks.onPickAsset?.(data);
      if (type === "error") {
        setError(data.message);
        callbacks.onError?.(data.message);
      }
      if (type === "snapshot") {
        const request = pending.current.get(data.requestId);
        if (request) {
          clearTimeout(request.timer);
          pending.current.delete(data.requestId);
          if (data.frame) {
            callbacks.onChange?.(data.frame);
            request.resolve(data.frame);
          } else request.reject(new Error("No item is loaded."));
        }
      }
    };
    window.addEventListener("message", listener);
    return () => {
      window.removeEventListener("message", listener);
      ready.current = false;
      syncVersion.current++;
      localBlobs.current.clear();
      pending.current.forEach(({ timer, reject }) => {
        clearTimeout(timer);
        reject(new Error("The editor closed."));
      });
      pending.current.clear();
    };
  }, [channel, html]);
  useEffect(() => {
    synchronize();
  }, [frame, active, disabled, assets, theme, fit, preload]);
  useEffect(() => {
    if (!context || !active) return;
    const rect = node.current?.getBoundingClientRect();
    if (rect) menu.current?.openAt(rect.left + context.x, rect.top + context.y);
  }, [context]);
  useEffect(() => {
    if (!active || disabled) menu.current?.close();
  }, [active, disabled]);
  const command = (command, data = {}) =>
    send("command", { command, frameId: frame?.id, ...data });
  const tool = (label, Icon, action, off = false, pressed) => (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={pressed}
      disabled={disabled || off}
      onClick={() => command(action)}
    >
      <Icon />
      <span>{label}</span>
    </button>
  );
  const shapeIcons = {
    rect: FiSquare,
    ellipse: FiCircle,
    triangle: FiTriangle,
    star: FiStar,
  };
  const menuItems = [
    ...(toolState.selected?.length === 1 &&
    toolState.selected[0].type === "text"
      ? [
          {
            key: "edit",
            label: "Edit text",
            icon: <FiEdit3 />,
            onPress: () => command("edit-text"),
          },
        ]
      : []),
    {
      key: "forward",
      label: "Bring forward",
      icon: <FiArrowUp />,
      onPress: () => command("forward"),
    },
    {
      key: "backward",
      label: "Send backward",
      icon: <FiArrowDown />,
      onPress: () => command("backward"),
    },
    {
      key: "duplicate",
      label: "Duplicate",
      icon: <FiCopy />,
      onPress: () => command("duplicate"),
    },
    {
      key: "delete",
      label: "Delete",
      icon: <FiTrash2 />,
      danger: true,
      onPress: () => command("delete"),
    },
  ];
  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
    >
      {editable && (
        <div className="post-edit-toolbar">
          {navigation && (
            <div className="post-edit-toolbar__navigation">{navigation}</div>
          )}
          <div className="post-edit-toolbar__actions">
            {tool("Undo", FiRotateCcw, "undo", !toolState.canUndo)}
            {tool("Redo", FiRotateCw, "redo", !toolState.canRedo)}
            {tool("Add rich text", FiType, "text")}
            {tool("Add image", FiImage, "image")}
            <DropdownMenu
              triggerLabel="Add shape"
              disabled={disabled}
              customBtn={
                <>
                  <FiSquare />
                  <span>Add shape</span>
                </>
              }
              style={{
                "--dm-z-index": 2147483450,
                "--dm-accent": "var(--brand)",
                "--dm-text": "var(--primary)",
              }}
              items={Object.entries(shapeIcons).map(([key, Icon]) => ({
                key,
                label: {
                  rect: "Rectangle",
                  ellipse: "Ellipse",
                  triangle: "Triangle",
                  star: "Star",
                }[key],
                icon: <Icon />,
                onPress: () => command("shape", { shape: key }),
              }))}
            />
            {tool("Add emoji", FiSmile, "sticker")}
            {tool(
              "Multiple selection",
              FiCopy,
              "multi",
              false,
              !!toolState.multi,
            )}
            {tool("Snap", FiGrid, "snap", false, toolState.snap !== false)}
          </div>
        </div>
      )}
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <iframe
          ref={node}
          title={editable ? "Post frame editor" : "Post frame"}
          srcDoc={html}
          sandbox="allow-scripts"
          onLoad={() => send("hello", {})}
          allow="autoplay"
          referrerPolicy="no-referrer"
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            border: 0,
            pointerEvents: editable ? "auto" : "none",
          }}
        />
        {controls && active && frame?.background?.kind === "video" && (
          <div
            className="post-video-controls"
            style={{
              position: "absolute",
              bottom: editable ? 44 : 4,
              left: 8,
              right: 8,
              zIndex: 24,
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "2px 5px",
              borderRadius: 8,
              background: "#171717cc",
              color: "#fff",
            }}
          >
            <button
              type="button"
              aria-label={playback.playing ? "Pause video" : "Play video"}
              onClick={() => command("play")}
              style={{
                border: 0,
                minHeight: 26,
                width: 28,
                padding: 4,
                color: "inherit",
                background: "transparent",
              }}
            >
              {playback.playing ? <FiPause /> : <FiPlay />}
            </button>
            <button
              type="button"
              aria-label={
                playback.muted !== false ? "Unmute video" : "Mute video"
              }
              onClick={() => command("mute")}
              style={{
                border: 0,
                minHeight: 26,
                width: 28,
                padding: 4,
                color: "inherit",
                background: "transparent",
              }}
            >
              {playback.muted !== false ? <FiVolumeX /> : <FiVolume2 />}
            </button>
            <input
              type="range"
              aria-label="Video position"
              min="0"
              max={playback.duration || frame.background.durationMs / 1000 || 1}
              step=".05"
              value={playback.time || 0}
              onChange={(e) =>
                command("seek", { time: Number(e.target.value) })
              }
              style={{
                flex: 1,
                minWidth: 0,
                padding: 0,
                height: 20,
                accentColor: "var(--brand,#1a82c4)",
              }}
            />
            <small style={{ fontSize: 10, whiteSpace: "nowrap" }}>
              {(playback.time || 0).toFixed(1)}s
            </small>
          </div>
        )}
      </div>
      {editable && (
        <div className="post-object-menu">
          <TinyContextMenu
            menuClassName="post-object-menu__panel"
            ref={menu}
            items={toolState.selected?.length ? menuItems : []}
            itemHeight={38}
            maxWidth={205}
            label="Object options"
          />
        </div>
      )}
      {error && editable && (
        <button
          type="button"
          onClick={() => setError("")}
          style={{
            position: "absolute",
            left: 8,
            bottom: 42,
            maxWidth: "90%",
            background: "#c8001e",
            color: "white",
          }}
        >
          {error} ×
        </button>
      )}
    </div>
  );
});
export default FrameSurface;
